import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Support = () => {
  const { user, gym } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Email form state
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: ''
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  // Theme detection
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkMode(document.body.classList.contains('dark-mode'));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const themeColors = {
    cardBg: isDarkMode ? '#1e293b' : '#ffffff',
    textPrimary: isDarkMode ? '#e2e8f0' : '#1a202c',
    textSecondary: isDarkMode ? '#94a3b8' : '#666666',
    borderColor: isDarkMode ? '#334155' : '#e2e8f0',
    inputBg: isDarkMode ? '#0f172a' : '#ffffff',
    messageBg: isDarkMode ? '#334155' : '#f1f5f9',
    userMessageBg: isDarkMode ? '#4CAF50' : '#4CAF50',
    supportMessageBg: isDarkMode ? '#475569' : '#e2e8f0'
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch chat history on mount
  useEffect(() => {
    if (activeTab === 'chat') {
      fetchChatHistory();
    }
  }, [activeTab]);

  const fetchChatHistory = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/support/chat', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      // Show welcome message if no chat history
      setMessages([{
        id: 'welcome',
        sender: 'support',
        message: 'Welcome to GymKhata Support! How can we help you today?',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || isSending) return;
    
    const messageText = newMessage.trim();
    setNewMessage('');
    setIsSending(true);

    // Add message to UI immediately
    const tempMessage = {
      id: Date.now(),
      sender: 'user',
      message: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/support/chat', {
        message: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add auto-reply after a short delay
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          sender: 'support',
          message: 'Thank you for your message! Our support team will respond shortly. For urgent issues, please email support@gymkhata.com',
          timestamp: new Date().toISOString()
        }]);
      }, 1000);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailForm.subject.trim() || !emailForm.message.trim()) return;
    
    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/support/email', {
        subject: emailForm.subject,
        message: emailForm.message,
        userEmail: user?.email,
        userName: user?.name,
        gymName: gym?.gymName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmailSuccess('Your message has been sent to support@gymkhata.com. We\'ll respond within 24 hours.');
      setEmailForm({ subject: '', message: '' });
      setTimeout(() => setEmailSuccess(''), 5000);
    } catch (error) {
      console.error('Failed to send email:', error);
      // Fallback: open email client
      window.location.href = `mailto:support@gymkhata.com?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(emailForm.message)}`;
    } finally {
      setEmailSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div>
      <div className="page-header">
        <h1>🎧 Support</h1>
      </div>

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        borderBottom: `2px solid ${themeColors.borderColor}`,
        paddingBottom: '10px'
      }}>
        <button
          onClick={() => setActiveTab('chat')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'chat' ? 'linear-gradient(135deg, #4CAF50, #45a049)' : 'transparent',
            color: activeTab === 'chat' ? 'white' : themeColors.textPrimary,
            border: activeTab === 'chat' ? 'none' : `1px solid ${themeColors.borderColor}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          💬 Live Chat
        </button>
        <button
          onClick={() => setActiveTab('email')}
          style={{
            padding: '12px 24px',
            background: activeTab === 'email' ? 'linear-gradient(135deg, #2196F3, #1976D2)' : 'transparent',
            color: activeTab === 'email' ? 'white' : themeColors.textPrimary,
            border: activeTab === 'email' ? 'none' : `1px solid ${themeColors.borderColor}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          ✉️ Email Support
        </button>
        <a
          href="mailto:support@gymkhata.com"
          style={{
            padding: '12px 24px',
            background: 'transparent',
            color: themeColors.textPrimary,
            border: `1px solid ${themeColors.borderColor}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none'
          }}
        >
          📧 Direct Email
        </a>
      </div>

      {/* Chat Tab */}
      {activeTab === 'chat' && (
        <div style={{
          background: themeColors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${themeColors.borderColor}`,
          overflow: 'hidden',
          maxWidth: '800px'
        }}>
          {/* Chat Header */}
          <div style={{
            background: 'linear-gradient(135deg, #4CAF50, #45a049)',
            padding: '15px 20px',
            color: 'white'
          }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                background: '#90EE90',
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></span>
              GymKhata Support
            </h3>
            <p style={{ margin: '5px 0 0', opacity: 0.9, fontSize: '13px' }}>
              We typically reply within a few hours
            </p>
          </div>

          {/* Messages Container */}
          <div style={{
            height: '400px',
            overflowY: 'auto',
            padding: '20px',
            background: themeColors.messageBg
          }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '50px', color: themeColors.textSecondary }}>
                Loading chat history...
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  style={{
                    display: 'flex',
                    justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    marginBottom: '15px'
                  }}
                >
                  <div style={{
                    maxWidth: '70%',
                    padding: '12px 16px',
                    borderRadius: msg.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: msg.sender === 'user' ? themeColors.userMessageBg : themeColors.supportMessageBg,
                    color: msg.sender === 'user' ? 'white' : themeColors.textPrimary
                  }}>
                    <p style={{ margin: 0, lineHeight: '1.5' }}>{msg.message}</p>
                    <span style={{
                      fontSize: '11px',
                      opacity: 0.7,
                      display: 'block',
                      marginTop: '5px',
                      textAlign: msg.sender === 'user' ? 'right' : 'left'
                    }}>
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div style={{
            padding: '15px 20px',
            borderTop: `1px solid ${themeColors.borderColor}`,
            display: 'flex',
            gap: '10px'
          }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px 16px',
                border: `1px solid ${themeColors.borderColor}`,
                borderRadius: '25px',
                outline: 'none',
                fontSize: '14px',
                background: themeColors.inputBg,
                color: themeColors.textPrimary
              }}
            />
            <button
              onClick={sendMessage}
              disabled={isSending || !newMessage.trim()}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                opacity: newMessage.trim() ? 1 : 0.6,
                fontWeight: '600'
              }}
            >
              {isSending ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* Email Tab */}
      {activeTab === 'email' && (
        <div style={{
          background: themeColors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${themeColors.borderColor}`,
          padding: '30px',
          maxWidth: '600px'
        }}>
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ margin: '0 0 10px', color: themeColors.textPrimary }}>
              ✉️ Email Support
            </h3>
            <p style={{ margin: 0, color: themeColors.textSecondary, fontSize: '14px' }}>
              Send us an email and we'll get back to you within 24 hours.
            </p>
          </div>

          {emailSuccess && (
            <div style={{
              padding: '15px',
              background: '#d4edda',
              color: '#155724',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              ✅ {emailSuccess}
            </div>
          )}

          <form onSubmit={handleEmailSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: themeColors.textPrimary
              }}>
                Subject
              </label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="What do you need help with?"
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid ${themeColors.borderColor}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: themeColors.inputBg,
                  color: themeColors.textPrimary,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: themeColors.textPrimary
              }}>
                Message
              </label>
              <textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Describe your issue or question in detail..."
                required
                rows={6}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid ${themeColors.borderColor}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical',
                  background: themeColors.inputBg,
                  color: themeColors.textPrimary,
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <button
                type="submit"
                disabled={emailSending}
                style={{
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {emailSending ? 'Sending...' : '📤 Send Email'}
              </button>
              <span style={{ color: themeColors.textSecondary, fontSize: '13px' }}>
                or email directly: <a href="mailto:support@gymkhata.com" style={{ color: '#2196F3' }}>support@gymkhata.com</a>
              </span>
            </div>
          </form>
        </div>
      )}

      {/* FAQ Section */}
      <div style={{
        marginTop: '30px',
        background: themeColors.cardBg,
        borderRadius: '12px',
        border: `1px solid ${themeColors.borderColor}`,
        padding: '25px',
        maxWidth: '800px'
      }}>
        <h3 style={{ margin: '0 0 20px', color: themeColors.textPrimary }}>
          ❓ Frequently Asked Questions
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {[
            { q: 'How do I add a new member?', a: 'Go to Members → Click "+ Add Member" → Fill in the details → Save' },
            { q: 'How do I record a payment?', a: 'Go to Payments → Click "+ Record Payment" → Select member and amount → Save' },
            { q: 'How do I export reports?', a: 'Go to Insights → Select the report type → Use the export button (coming soon)' },
            { q: 'How do I change my password?', a: 'Go to Settings → Account → Change Password' },
            { q: 'How do I contact support?', a: 'Use the Live Chat above or email support@gymkhata.com' }
          ].map((faq, i) => (
            <div key={i} style={{
              padding: '15px',
              background: themeColors.messageBg,
              borderRadius: '8px',
              borderLeft: '4px solid #4CAF50'
            }}>
              <p style={{ margin: '0 0 8px', fontWeight: '600', color: themeColors.textPrimary }}>
                {faq.q}
              </p>
              <p style={{ margin: 0, color: themeColors.textSecondary, fontSize: '14px' }}>
                {faq.a}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Support;
